import * as React from "react";

import { cn } from "@/lib/utils";

import { Kbd } from "./kbd";

type PeekPanelProps = React.ComponentProps<"aside">;

/**
 * Linear's Peek: a 360px right panel that previews the focused list row
 * without leaving the list. Space closes it, ↑↓ move it, ↵ opens the record.
 */
function PeekPanel({ className, children, ...props }: PeekPanelProps) {
  return (
    <aside
      data-slot="peek-panel"
      className={cn(
        "flex min-h-0 w-90 shrink-0 flex-col border-l border-line-2 bg-surface",
        className
      )}
      {...props}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-4 pt-3.5 pb-4">
        {children}
      </div>
      <footer className="flex h-9 shrink-0 items-center gap-2.5 border-t border-line-2 px-4 text-sm text-fg-3">
        <span className="flex items-center gap-1">
          <Kbd>Space</Kbd> Close
        </span>
        <span className="flex items-center gap-1">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd> Next Row
        </span>
        <span className="flex-1" />
        <span className="flex items-center gap-1">
          <Kbd>↵</Kbd> Open
        </span>
      </footer>
    </aside>
  );
}

type PeekPropsProps = React.ComponentProps<"dl">;

/** The property list: a 112px muted label column, values on the right. */
function PeekProps({ className, ...props }: PeekPropsProps) {
  return (
    <dl
      data-slot="peek-props"
      className={cn("flex flex-col", className)}
      {...props}
    />
  );
}

type PeekPropProps = Omit<React.ComponentProps<"div">, "title"> & {
  label: React.ReactNode;
  /** `start` for values that wrap (tags, long text). */
  align?: "center" | "start";
};

function PeekProp({
  label,
  align = "center",
  className,
  children,
  ...props
}: PeekPropProps) {
  return (
    <div
      data-slot="peek-prop"
      className={cn(
        "grid min-h-7 grid-cols-[7rem_minmax(0,1fr)] gap-x-2 py-0.5 text-base",
        align === "center" ? "items-center" : "items-start",
        className
      )}
      {...props}
    >
      <dt className={cn("text-fg-3", align === "start" && "pt-1")}>{label}</dt>
      <dd className="flex min-w-0 flex-wrap items-center gap-1.5 text-fg">
        {children}
      </dd>
    </div>
  );
}

type PeekActionsProps = React.ComponentProps<"div">;

/** At most two action rows computed from the record, in one outlined card. */
function PeekActions({ className, ...props }: PeekActionsProps) {
  return (
    <div
      data-slot="peek-actions"
      className={cn(
        "flex flex-col divide-y divide-line-1 overflow-hidden rounded-xl bg-elevated ring-1 ring-line-2",
        className
      )}
      {...props}
    />
  );
}

type PeekActionProps = Omit<React.ComponentProps<"div">, "title"> & {
  icon?: React.ComponentType<{ className?: string }>;
  /** Tone of the leading icon. */
  tone?: "neutral" | "warn";
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
};

function PeekAction({
  icon: Icon,
  tone = "neutral",
  title,
  description,
  action,
  className,
  ...props
}: PeekActionProps) {
  return (
    <div
      data-slot="peek-action"
      className={cn("flex items-start gap-2 px-3 py-2.5", className)}
      {...props}
    >
      {Icon ? (
        <Icon
          className={cn(
            "mt-0.5 size-3.5 shrink-0",
            tone === "warn" ? "text-warn" : "text-fg-3"
          )}
        />
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-base text-fg">{title}</span>
        {description ? (
          <span className="text-sm text-fg-3">{description}</span>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return Boolean(
    target.closest(
      'input, textarea, select, button, a[href], [role="button"], [role="checkbox"], [role="switch"], [role="tab"], [role="dialog"], [role="menu"], [role="listbox"]'
    )
  );
};

type UseListPeekOptions = {
  /** Row ids in display order. */
  ids: string[];
  /** ↵ on the focused or peeked row. */
  onOpen: (id: string) => void;
  /** Called when keyboard focus lands on a row, to scroll it into view. */
  onFocusRow?: (id: string) => void;
  enabled?: boolean;
};

type ListPeek = {
  /** The keyboard-focused row (selected style). */
  activeId: string | null;
  /** The row shown in the peek; null when closed. */
  peekId: string | null;
  /** Report the row under the pointer; Space peeks it. */
  setHoverId: (id: string | null) => void;
  toggle: (id?: string | null) => void;
  close: () => void;
};

/**
 * Keyboard layer for a `ListPage`: Space peeks the focused or hovered row,
 * ↑↓ / J K move, ↵ opens, Esc closes. Ignores keys typed into fields and
 * keys pressed inside dialogs and menus.
 */
function useListPeek({
  ids,
  onOpen,
  onFocusRow,
  enabled = true
}: UseListPeekOptions): ListPeek {
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [peekId, setPeekId] = React.useState<string | null>(null);
  const hoverRef = React.useRef<string | null>(null);
  const latest = React.useRef({ ids, onOpen, onFocusRow, activeId, peekId });
  React.useLayoutEffect(() => {
    latest.current = { ids, onOpen, onFocusRow, activeId, peekId };
  });

  React.useEffect(() => {
    if (activeId && !ids.includes(activeId)) setActiveId(null);
    if (peekId && !ids.includes(peekId)) setPeekId(null);
  }, [activeId, ids, peekId]);

  const setHoverId = React.useCallback((id: string | null) => {
    hoverRef.current = id;
  }, []);

  const close = React.useCallback(() => setPeekId(null), []);

  const toggle = React.useCallback((id?: string | null) => {
    const cur = latest.current;
    if (cur.peekId && (!id || id === cur.peekId)) {
      setPeekId(null);
      return;
    }
    const target = id ?? cur.activeId ?? hoverRef.current ?? cur.ids[0];
    if (!target) return;
    setActiveId(target);
    setPeekId(target);
  }, []);

  React.useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      const cur = latest.current;
      const navigating = Boolean(cur.peekId || cur.activeId);
      const move = (delta: number) => {
        if (!cur.ids.length) return;
        const from = cur.activeId ?? hoverRef.current;
        const index = from ? cur.ids.indexOf(from) : -1;
        const next =
          index < 0
            ? cur.ids[delta > 0 ? 0 : cur.ids.length - 1]
            : cur.ids[Math.min(cur.ids.length - 1, Math.max(0, index + delta))];
        setActiveId(next);
        if (cur.peekId) setPeekId(next);
        cur.onFocusRow?.(next);
      };
      switch (e.key) {
        case " ":
          if (!cur.ids.length) return;
          e.preventDefault();
          toggle();
          return;
        case "ArrowDown":
        case "ArrowUp":
          if (!navigating) return;
          e.preventDefault();
          move(e.key === "ArrowDown" ? 1 : -1);
          return;
        case "j":
        case "k":
          e.preventDefault();
          move(e.key === "j" ? 1 : -1);
          return;
        case "Enter": {
          const id = cur.peekId ?? cur.activeId;
          if (!id) return;
          e.preventDefault();
          cur.onOpen(id);
          return;
        }
        case "Escape":
          if (cur.peekId) {
            e.preventDefault();
            setPeekId(null);
          } else if (cur.activeId) {
            setActiveId(null);
          }
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled, toggle]);

  return { activeId, peekId, setHoverId, toggle, close };
}

export { PeekAction, PeekActions, PeekPanel, PeekProp, PeekProps, useListPeek };
export type {
  ListPeek,
  PeekActionProps,
  PeekPanelProps,
  PeekPropProps,
  UseListPeekOptions
};
